"use client";

import { useSearchParams } from "next/navigation";
import { type FormEvent, useMemo, useState } from "react";

type LoginState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "error"; message: string };

export default function LoginForm() {
  const searchParams = useSearchParams();
  const redirectTarget = useMemo(() => {
    const redirect = searchParams.get("redirect");
    // Prevent redirect loops to login page
    return redirect && redirect !== "/login" && !redirect.startsWith("/login")
      ? redirect
      : "/";
  }, [searchParams]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [state, setState] = useState<LoginState>({ status: "idle" });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setState({ status: "submitting" });

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as Partial<{
          error: string;
        }>;
        setState({
          status: "error",
          message: data.error || `Login failed (${res.status})`,
        });
        return;
      }

      // Use full page redirect to ensure cookies are properly applied
      globalThis.location.href = redirectTarget;
    } catch (err) {
      setState({
        status: "error",
        message: err instanceof Error ? err.message : "Login failed",
      });
    }
  };

  return (
    <div className="w-full max-w-sm rounded-2xl border border-black/10 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-black">
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-black dark:text-slate-50">
          Sign in
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Use your QuoteWell account.
        </p>
      </div>

      {state.status === "error" ? (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {state.message}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Email
          </span>
          <input
            className="h-11 rounded-lg border border-black/10 bg-transparent px-3 text-sm text-black outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:text-slate-50 dark:focus:ring-white/20"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Password
          </span>
          <input
            className="h-11 rounded-lg border border-black/10 bg-transparent px-3 text-sm text-black outline-none focus:ring-2 focus:ring-black/20 dark:border-white/10 dark:text-slate-50 dark:focus:ring-white/20"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          Remember me
        </label>

        <button
          type="submit"
          disabled={state.status === "submitting"}
          className="mt-2 inline-flex h-11 items-center justify-center rounded-lg bg-black px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-slate-200"
        >
          {state.status === "submitting" ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}
